<template>
  <f7-page>
    <AppLayout>
      <template #main>
    <f7-block v-if="loading" class="loading-block">
      <f7-preloader color="green" />
    </f7-block>

    <template v-else>
      <!-- Health -->
      <f7-card class="growth-card health-card">
        <f7-card-content>
          <div class="health-head">
            <div class="health-ring" :style="{ borderColor: healthMeta.color }">
              <span class="health-score" :style="{ color: healthMeta.color }">
                {{ health.score ?? 0 }}%
              </span>
            </div>
            <div class="health-info">
              <div class="t-label">{{ $t('growth.tab.healthTitle') }}</div>
              <span class="health-badge" :style="{ background: healthMeta.bg, color: healthMeta.color }">
                {{ $t(healthMeta.label) }}
              </span>
              <div class="t-muted t-small">{{ $t('growth.tab.vitalityScore') }}</div>
            </div>
          </div>
          <f7-button small outline class="btn-detail" @click="goTo('health')">
            {{ $t('growth.tab.viewDetail') }}
          </f7-button>
        </f7-card-content>
      </f7-card>

      <!-- Trust + Credits -->
      <div class="split-row">
        <f7-card class="growth-card trust-card">
          <f7-card-content>
            <div class="trust-icon" :style="{ color: trustMeta.color }">
              <f7-icon :f7="trustMeta.icon" size="26" />
            </div>
            <div class="t-label">{{ $t('growth.tab.trust') }}</div>
            <div class="trust-value" :style="{ color: trustMeta.color }">{{ $t(trustMeta.label) }}</div>
            <div class="t-muted t-xs">{{ trust.trustPoints }} {{ $t('growth.tab.pts') }}</div>
            <f7-progressbar
              :progress="trustPct"
              class="progress-gold"
            ></f7-progressbar>
          </f7-card-content>
        </f7-card>

        <f7-card class="growth-card credit-card">
          <f7-card-content>
            <div class="trust-icon t-gold">
              <f7-icon f7="wallet" size="26" />
            </div>
            <div class="t-label">{{ $t('growth.tab.credits') }}</div>
            <div class="credit-value">{{ $t('growth.tab.availableCredit') }}</div>
            <div class="t-muted t-xs">{{ wallet.availableCredit }} {{ $t('growth.tab.availableXof') }}</div>
            <f7-button small outline class="btn-detail" @click="goTo('spend-credits')">
              {{ $t('growth.tab.spend') }}
            </f7-button>
          </f7-card-content>
        </f7-card>
      </div>

      <!-- Achievements -->
      <f7-card class="growth-card achievements-card">
        <f7-card-header>
          <f7-icon f7="rosette" size="22" class="t-purple" />
          <div class="t-label" v-html="$t('growth.tab.rewardsBadges')"></div>
          <f7-link class="t-purple" @click="goTo('achievements')">{{ $t('growth.tab.viewAll') }}</f7-link>
        </f7-card-header>
        <f7-card-content>
          <div v-if="achievements.length === 0" class="t-muted t-small py-2">
            {{ $t('growth.tab.noBadges') }}
          </div>
          <div v-else class="badge-row">
            <f7-chip
              v-for="a in achievements"
              :key="a.id"
              :text="a.badge"
              outline
              class="badge-chip badge-purple"
            >
              <f7-icon f7="star" slot="icon" class="t-purple"></f7-icon>
            </f7-chip>
          </div>
        </f7-card-content>
      </f7-card>

      <!-- Milestones preview -->
      <f7-card v-if="milestones.length" class="growth-card">
        <f7-card-header>
          <f7-icon f7="flag_fill" size="22" class="t-blue" />
          <div class="t-label">{{ $t('growth.tab.keyMilestones') }}</div>
        </f7-card-header>
        <f7-card-content>
          <f7-list class="growth-list">
            <f7-list-item
              v-for="m in milestones.slice(0, 3)"
              :key="m.id"
              :title="m.name"
              :subtitle="m.completed ? $t('growth.tab.completed') : $t('growth.tab.inProgress')"
            >
              <template #after>
                <f7-icon
                  :f7="m.completed ? 'checkmark_alt_circle_fill' : 'circle'"
                  :class="m.completed ? 't-green' : 't-muted'"
                  size="20"
                />
              </template>
            </f7-list-item>
          </f7-list>
        </f7-card-content>
      </f7-card>
    </template>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { f7 } from 'framework7-vue'
import AppLayout from '../common/AppLayout.vue'
import { useGrowthStore } from '../../js/pinia-stores/growth.js'
import {
  trustMeta as trustMetaOf, trustProgress, healthMeta as healthMetaOf,
} from '../../constants/growth-levels.js'
import i18n from '../../js/i18n.js'

const { t } = i18n.global

const props = defineProps({ groupId: { type: String, required: true } })

const growthStore = useGrowthStore()

const wallet = computed(() => growthStore.wallet)
const trust = computed(() => growthStore.trust)
const health = computed(() => growthStore.health)
const milestones = computed(() => growthStore.milestones)
const achievements = computed(() => growthStore.achievements)
const loading = computed(() => growthStore.loading)

const trustMeta = computed(() => trustMetaOf(trust.value.trustLevel))
const trustPct = computed(() => trustProgress(trust.value.trustPoints, trust.value.trustLevel))
const healthMeta = computed(() => healthMetaOf(health.value.score))

onMounted(async () => {
  try {
    await Promise.all([
      growthStore.fetchDashboard(props.groupId),
      growthStore.fetchHealth(props.groupId),
    ])
  } catch (e) {
    console.warn('Growth tab data unavailable:', e)
  }
})

function goTo(section) {
  const g = props.groupId
  const path = {
    health: `/groups/${g}/growth/health/`,
    achievements: `/groups/${g}/growth/achievements/`,
    'spend-credits': `/groups/${g}/growth/spend-credits/`,
    dashboard: `/groups/${g}/growth/`,
  }[section]
  f7.views.main.router.navigate(path)
}
</script>

<style scoped>
.growth-card {
  --avo-green: var(--avocash-green, #1a6b42);
  --avo-green-light: var(--avocash-green-light, #eaf2eb);
  --avo-gold: var(--avocash-gold, #c9a84c);
  --avo-gold-light: var(--avocash-gold-light, #fdf6e3);
  --avo-blue: var(--avocash-blue, #3b4fca);
  --avo-purple: var(--avocash-purple, #6b46c1);
  --avo-purple-light: var(--avocash-purple-light, #f5f0ff);
  --avo-surface: var(--avocash-surface, #f4f7f2);
  --avo-pit: var(--avocash-pit, #1c1c1e);
  border-radius: 14px;
  margin-bottom: 12px;
}
.growth-card :deep(.card-header) { font-size: 14px; gap: 8px; }

.loading-block { text-align: center; padding: 30px 0; }

.t-label { font-size: 13px; font-weight: 600; }
.t-muted { color: var(--avo-pit); opacity: .6; }
.t-small { font-size: 12px; }
.t-xs { font-size: 11px; }
.t-green { color: var(--avo-green); }
.t-gold { color: var(--avo-gold); }
.t-blue { color: var(--avo-blue); }
.t-purple { color: var(--avo-purple); }

/* Health */
.health-card :deep(.card-content) { padding-top: 14px; }
.health-head { display: flex; align-items: center; gap: 12px; }
.health-ring {
  width: 72px; height: 72px; border-radius: 50%;
  border: 4px solid var(--avo-green); background: var(--f7-card-bg-color, #fff);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.health-score { font-size: 20px; font-weight: 700; }
.health-info { flex: 1; min-width: 0; }
.health-info .t-muted { margin-top: 3px; }
.health-badge {
  display: inline-flex; padding: 3px 10px; border-radius: 10px;
  font-size: 11px; font-weight: 600; margin-top: 4px;
}

/* Split trust / credits */
.split-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.trust-card, .credit-card { text-align: center; }
.trust-card { background: var(--avo-gold-light); }
.credit-card { background: var(--avo-green-light); }
.trust-card :deep(.card-content), .credit-card :deep(.card-content) {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
}
.trust-icon { line-height: 1; }
.trust-value { font-size: 18px; font-weight: 700; text-transform: capitalize; }
.credit-value { font-size: 20px; font-weight: 700; color: var(--avo-green); }
.progress-gold { width: 100%; margin-top: 6px; }
.progress-gold :deep(.progressbar-inner) { background: var(--avo-gold); }
.btn-detail { margin-top: 8px; --f7-button-border-color: var(--avo-green); --f7-button-text-color: var(--avo-green); }

/* Achievements */
.achievements-card { background: linear-gradient(180deg, var(--avo-purple-light), transparent); }
.achievements-card :deep(.card-header) .right { margin-left: auto; }
.badge-row { display: flex; flex-wrap: wrap; gap: 6px; }
.badge-chip { margin: 0; }
.badge-purple :deep(.chip-label) { color: var(--avo-purple); }
.badge-purple :deep(.chip-outline) { border-color: var(--avo-purple); }

.growth-list :deep(.item-title) { font-size: 13px; }
.growth-list :deep(.item-subtitle) { font-size: 11px; }
</style>
